import { Observable } from 'rxjs/Observable';
import { Component, ViewChild, OnDestroy, EventEmitter, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs/Subscription';
import { isEmpty } from 'lodash';

import { KeybindService, KEYBOARD_CODES, KeybindDescription } from '~core/services';
import { VerificationComponent, SignInComponent } from '~auth/components';
import { RegistrationService, ModalService, UserService } from '~auth/services';
import { emailValidator, matchingPasswords, noWhitespaceValidator } from '~auth/validators/validators';
import { UserProfile } from '~auth/model';

@Component({
  selector: 'uw-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SignUpComponent implements OnDestroy {
  @ViewChild('signUp') signUpContent;

  signUpModal: NgbModalRef;
  signupForm: FormGroup;
  close = new EventEmitter();
  subscriptions: Subscription[] = [];
  progressVisible: boolean;
  successResponse: string;
  failureResponse: string;

  constructor(private ngbModal: NgbModal,
    private fb: FormBuilder,
    private keybindService: KeybindService,
    private userService: UserService,
    private registrationService: RegistrationService,
    private modalService: ModalService) {
    this.signupForm = this.fb.group({
      firstName: ['', Validators.required, this.noWhitespaceValidator],
      lastName: ['', Validators.required, this.noWhitespaceValidator],
      email: ['', Validators.required, this.validateEmail],
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required]
    }, { validator: this.validateConfirmPasswords('password', 'confirmPassword') });

    this.subscriptions.push(this.keybindService.bind(new KeybindDescription(KEYBOARD_CODES.escape))
      .subscribe(x => this.closeModal()));
  }

  noWhitespaceValidator(control: FormControl) {
    let isWhitespace = (control.value || '').trim().length === 0;
    return Observable.of(isWhitespace ? { 'whitespace': true } : null);
  }

  validateEmail(control: FormControl): { [key: string]: any } {
    const emailRegexp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
    let result = false;
    if (control.value && !emailRegexp.test(control.value)) {
      result = true;
    }
    return Observable.of(result ? { invalidEmail: true } : null);
  }

  validateConfirmPasswords(passwordKey: string, confirmPasswordKey: string) {
    return (group: FormGroup): { [key: string]: any } => {
      const password = group.controls[passwordKey];
      const confirmPassword = group.controls[confirmPasswordKey];

      if (password.value !== confirmPassword.value) {
        return {
          mismatchedPasswords: true
        };
      }
    };
  }

  createUser(data) {
    this.progressVisible = true;
    this.successResponse = '';
    this.failureResponse = '';
    this.registrationService.registerUser(data)
      .take(1)
      .subscribe(
        (success: any) => {
          this.failureResponse = '';
          this.openRegistrationModal(this.signupForm.get('email').value, this.signupForm.get('password').value);
          this.progressVisible = false;
        }, error => {
          this.successResponse = '';
          this.failureResponse = 'User Creation Failed. Please contact admin.';
          this.progressVisible = false;
        });
  }

  open(params: any) {
    this.signupForm.reset();
    this.signUpModal = this.ngbModal.open(this.signUpContent, {
      keyboard: false,
      backdrop: 'static',
      windowClass: 'signup-modal'
    });
    return this.signUpModal.result;
  }

  openRegistrationModal(email, password) {
    this.failureResponse = '';
    this.successResponse = '';
    this.userService.signIn(email, password)
      .take(1)
      .subscribe((user: UserProfile) => {
        if (!user || isEmpty(user)) { // UserProfile is Null, Verification is not complete
          this.modalService.getModalComponent(VerificationComponent, { email: email, pwd: password });
        }

        this.progressVisible = false;
        this.closeModal();
      },
        err => {
          this.failureResponse = 'User Creation Failed. Please contact admin.';
          this.progressVisible = false;
        });
  }

  closeModal() {
    this.close.emit('CLOSE');
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    this.signUpModal.close();
  }

  goBack() {
    this.modalService.getModalComponent(SignInComponent);
    this.closeModal();
  }

  ngOnDestroy() {
    this.close.complete();
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }
}



// WEBPACK FOOTER //
// /azp/_work/1/s/src/app/auth/components/signup.component.ts